import React, { useEffect, useState } from 'react';
import moment from 'moment';
import _ from 'lodash';
// TODO: Migrate module so that you don’t import from the corp project in a global module
/* eslint-disable restrict-imports/restrict-imports */
import Select from 'corp/components/UI/Select';
import InputText from 'design-system/InputText';
import { Button, Label } from 'design-system/Form';
import DateRangePicker from 'design-system/DateRangePicker';
import NumericInput from 'design-system/NumericInput';
import SelectProject from 'corp/components/UI/SelectProject';
import { Section } from 'react-scale/containers/corp/backfills/task/components/Section';
import apiFetch from '@scale/frontend-lib/api-fetch';
import { TaskListItem } from '@scale/shared/types/corp/task/taskListItem';
import { useSearchParams } from 'mist/hooks';
import { TaskType, TaskQueryOptionalData } from '@scale/shared-types';
import { Checkbox } from 'react-scale/components/design-system';
import { IAction } from '../types';
import { useMount } from 'react-use-v13';

const taskStatusOptions = [
  { value: 'all', label: 'All' },
  { value: 'pending', label: 'Pending' },
  { value: 'completed', label: 'Completed' },
  { value: 'error', label: 'Error' },
  { value: 'canceled', label: 'Canceled' },
];

const callbackCompletedOptions = [
  { value: 'all', label: 'All' },
  { value: 'null', label: 'null (not assigned)' },
  { value: true, label: 'true' },
  { value: false, label: 'false' },
];

const customerReviewStatusOptions = [
  { value: 'all', label: 'All' },
  { value: 'ready-to-review', label: 'Ready to Audit' },
  { value: 'review-accepted', label: 'Accepted' },
  { value: 'review-fixed', label: 'Fixed' },
  { value: 'review-rejected', label: 'Rejected' },
];

interface FetchTasks {
  tasks: TaskListItem[];
  taskToSubtaskMapping?: Partial<Record<string, string>>;
  error: any;
  isLoading: boolean;
}

const defaultFetchTasks: FetchTasks = {
  tasks: [],
  error: null,
  isLoading: false,
};

const Loader = () => <h3>Loading ...</h3>;

const Error = ({ error }: any) => <h3>{JSON.stringify(error)}</h3>;

interface QueryTasksProps {
  onChange: (tasks: TaskListItem[]) => void;
  onOptDataChange?: (optData: TaskQueryOptionalData) => void;
  taskType?: TaskType;
  disallowedTaskTypes?: TaskType[];
  filterSelectedTasks?: any[];
  setLoading: (loading: boolean) => void;
  warning?: string;
  onProjectSelect?: (project: { value: string; label: string } | null | undefined) => void;
  action?: IAction;
  setProtectFromCancellingTasks?: (value: boolean) => void;
  lockToProjectId?: string;
}

// eslint-disable-next-line complexity
const QueryTasks: React.FC<QueryTasksProps> = ({
  onChange,
  onOptDataChange,
  taskType,
  filterSelectedTasks = [],
  disallowedTaskTypes,
  setLoading,
  warning = '',
  onProjectSelect = undefined,
  action,
  setProtectFromCancellingTasks,
  lockToProjectId,
}) => {
  const preselectedBatchId = useSearchParams('batchId');
  const [query, setQuery] = useState<any>({});
  const [fetchTasks, setFetchTasks] = useState<FetchTasks>(defaultFetchTasks);
  const [selectedTasks, setSelectedTasks] = useState<any[]>(fetchTasks.tasks);
  const [batchOptions, setBatchOptions] = useState<any[]>([]);
  const [tasksFilter, setTasksFilter] = useState<string>();

  useEffect(() => {
    onChange(selectedTasks);
  }, [onChange, selectedTasks]);

  useEffect(() => {
    if (filterSelectedTasks.length) {
      handleTasksFilter(filterSelectedTasks.join(', '));
      setSelectedTasks(
        _.filter(fetchTasks.tasks, task => filterSelectedTasks.find(fTask => task._id === fTask)),
      );
    }
  }, [filterSelectedTasks]);

  useEffect(() => {
    setSelectedTasks(fetchTasks.tasks);
    onOptDataChange && onOptDataChange({ taskToSubtaskMapping: fetchTasks.taskToSubtaskMapping });
  }, [fetchTasks.tasks, fetchTasks.taskToSubtaskMapping]);

  useEffect(() => {
    setLoading(fetchTasks.isLoading);
  }, [setLoading, fetchTasks.isLoading]);

  const [filters, setFilters] = useState({
    project: null,
    status: taskStatusOptions[0],
    customerReviewStatus: customerReviewStatusOptions[0],
    from: moment.utc().subtract(1, 'year').startOf('day'),
    to: moment.utc().endOf('day'),
    completedFrom: moment.utc().subtract(1, 'year').startOf('day'),
    completedTo: moment.utc().endOf('day'),
    limit: 0,
    batch: null,
    tag: '',
    callbackCompleted: callbackCompletedOptions[0],
    taskIds: [],
    useSubtaskForCloning: false,
  });

  useMount(() => {
    if (lockToProjectId) {
      handleChange('project', { label: lockToProjectId, value: lockToProjectId });
    }
  });

  useEffect(() => {
    if (filters.project) {
      handleBatchSearch(filters.project);
    }
    if (onProjectSelect) onProjectSelect(filters.project);
  }, [filters.project]);

  useEffect(() => {
    if (preselectedBatchId && batchOptions.length) {
      const batch = batchOptions.find(x => x.value === preselectedBatchId);
      if (batch) {
        handleChange('batch', batch);
      }
    }
  }, [preselectedBatchId, batchOptions]);

  useEffect(() => {
    if (preselectedBatchId && filters.batch) {
      handleSearch();
    }
  }, [preselectedBatchId, filters.batch]);

  const filtersToQuery = (newFilters: $FIXME) => ({
    project: _.get(newFilters, 'project.value'),
    limit: _.get(newFilters, 'limit') === 0 ? undefined : _.get(newFilters, 'limit'),
    status: _.get(newFilters, 'status.value'),
    from: newFilters.from && newFilters.from.toISOString(),
    to: newFilters.to && newFilters.to.toISOString(),
    completedFrom:
      _.get(newFilters, 'status.value') === 'completed'
        ? newFilters.completedFrom.toISOString()
        : undefined,
    completedTo:
      _.get(newFilters, 'status.value') === 'completed'
        ? newFilters.completedTo.toISOString()
        : undefined,
    customerReviewStatus:
      _.get(newFilters, 'status.value') === 'completed'
        ? _.get(newFilters, 'customerReviewStatus.value')
        : undefined,
    batch: _.get(newFilters, 'batch.value'),
    tag: _.get(newFilters, 'tag'),
    callbackCompleted: _.get(newFilters, 'callbackCompleted.value'),
    taskIds: _.get(newFilters, 'taskIds'),
    useSubtaskForCloning: _.get(newFilters, 'useSubtaskForCloning'),
  });

  const handleSearch = async () => {
    if (!filters.project) {
      alert('A project is required, and only one project can be actioned at a time.'); // TODO: add a proper form error handler
    } else {
      setFetchTasks({ ...fetchTasks, ...{ isLoading: true } });
      try {
        const { tasks, taskToSubtaskMapping, protectFromCancellingTasks } = await apiFetch(
          null,
          'corp-api/task-actions/task',
          {
            method: 'POST',
            body: query,
          },
        );
        setFetchTasks({
          tasks,
          taskToSubtaskMapping,
          error: false,
          isLoading: false,
        });
        if (setProtectFromCancellingTasks) {
          setProtectFromCancellingTasks(protectFromCancellingTasks);
        }
      } catch (e) {
        setFetchTasks({
          tasks: [],
          taskToSubtaskMapping: undefined,
          error: e,
          isLoading: false,
        });
      }
    }
  };

  const handleChange = (name: $FIXME, value: $FIXME, search: boolean = true) => {
    const newFilters = { ...filters, [name]: value };
    setFilters(newFilters);
    if (search) {
      setQuery(filtersToQuery(newFilters));
    }
  };

  const handleBatchSearch = async (project: Record<string, string> | null) => {
    if (project) {
      try {
        const response = await apiFetch(null, 'corp-api/task-actions-lite/project-batches', {
          method: 'POST',
          body: { project: project.value },
        });
        setBatchOptions(
          response.batches.map((batch: Record<string, string>) => {
            return { label: batch.name, value: batch._id };
          }),
        );
      } catch (e) {
        setFetchTasks({
          tasks: [],
          error: e,
          isLoading: false,
        });
      }
    }
  };

  const handleTasksFilter = (value: any) => {
    setTasksFilter(value);
    const taskIds = value.split(/[^A-Zaa-z0-9]/).filter((e: string) => e.length && e != null);
    if (taskIds.length) {
      handleChange('taskIds', taskIds);
    } else {
      handleChange('taskIds', []);
    }
  };

  return (
    <Section name={`Query Tasks (${fetchTasks.tasks ? fetchTasks.tasks.length : 0})`}>
      <div className="description">
        Use this section to set the parameters to query tasks. Leave non-relevant sections blank.
      </div>
      <div className={fetchTasks.isLoading ? 'disabled root' : 'root'}>
        {fetchTasks.isLoading && <Loader />}
        {fetchTasks.error && <Error error={fetchTasks.error} />}
        <div className="section-rows">
          <section className="disallowed-task-types">
            {disallowedTaskTypes ? `Disallowed Task Types: ${disallowedTaskTypes.join(', ')}` : ''}
          </section>
          <section className="warning">{warning ? `Warning: ${warning}` : ''}</section>
          <section className="three-columns">
            <section>
              <Label>Project: </Label>
              {lockToProjectId ? (
                <div>{lockToProjectId}</div>
              ) : (
                <SelectProject
                  required
                  disabled={fetchTasks.isLoading}
                  // @ts-ignore
                  value={filters.project}
                  // @ts-ignore FIXME - Typescript Strict [7006]
                  onChange={value => handleChange('project', value)}
                  disallowedTaskTypes={disallowedTaskTypes}
                  taskType={taskType}
                />
              )}
            </section>
            <section className={!filters?.project ? 'disabled' : ''}>
              <Label>Result Limit (0 is no limit):</Label>
              <NumericInput
                value={filters.limit}
                onChange={(value: $FIXME) => handleChange('limit', value)}
                size={'md'}
                disabled={filters?.project === null || fetchTasks.isLoading}
                positiveOnly={true}
              />
            </section>
            <section className={!filters?.project ? 'disabled' : ''}>
              <Label>Batch:</Label>
              <Select
                value={filters.batch}
                disabled={filters?.project === null || fetchTasks.isLoading}
                onChange={(value: $FIXME) => handleChange('batch', value)}
                options={batchOptions}
                getOptionLabel={(option: Record<string, string>) => option.name}
                getOptionValue={(option: Record<string, string>) => option._id}
              />
            </section>
          </section>
          <section className="three-columns">
            <section>
              <Label>Status:</Label>
              <Select
                required
                disabled={fetchTasks.isLoading}
                value={filters.status}
                onChange={(value: $FIXME) => handleChange('status', value)}
                options={taskStatusOptions}
              />
            </section>
            <section className={!filters?.project ? 'disabled' : ''}>
              <Label>Tags (comma-delimited without quotes and spaces):</Label>
              <InputText
                type="text"
                value={filters.tag}
                disabled={filters?.project === null || fetchTasks.isLoading}
                onChange={(value: $FIXME) => handleChange('tag', value.value, false)}
                onKeyPress={(e: React.KeyboardEvent) => {
                  if (e.key === 'Enter') {
                    handleChange('tag', (e.target as HTMLInputElement).value);
                  }
                }}
              />
            </section>
            <section className={!filters?.project ? 'disabled' : ''}>
              <Label>Callback Completed:</Label>
              <Select
                required
                disabled={filters?.project === null || fetchTasks.isLoading}
                value={filters.callbackCompleted}
                onChange={(value: $FIXME) => handleChange('callbackCompleted', value)}
                options={callbackCompletedOptions}
              />
            </section>
          </section>
          <section className="three-columns">
            <section className={!filters?.project ? 'disabled' : ''}>
              <Label>Creation Date:</Label>
              <DateRangePicker
                disabled={fetchTasks.isLoading}
                className="date-range"
                to={filters.to}
                from={filters.from}
                setTo={(value: $FIXME) => handleChange('to', value)}
                setFrom={(value: $FIXME) => handleChange('from', value)}
              />
            </section>
            <section className={filters.status?.value !== 'completed' ? 'disabled' : ''}>
              <Label>Completed Date:</Label>
              <DateRangePicker
                className="date-range"
                to={filters.completedTo}
                from={filters.completedFrom}
                disabled={filters.status?.value !== 'completed' || fetchTasks.isLoading}
                setTo={(value: $FIXME) => handleChange('completedTo', value)}
                setFrom={(value: $FIXME) => handleChange('completedFrom', value)}
              />
            </section>
            <section className={filters.status?.value !== 'completed' ? 'disabled' : ''}>
              <Label>Audit state:</Label>
              <Select
                value={filters.customerReviewStatus}
                disabled={
                  (filters.status && filters.status.value !== 'completed') || fetchTasks.isLoading
                }
                onChange={(value: $FIXME) => handleChange('customerReviewStatus', value)}
                options={customerReviewStatusOptions}
              />
            </section>
          </section>
          {action?.showSubtaskOption && (
            <section className="three-columns">
              <section className={!filters?.project ? 'disabled' : ''}>
                <Checkbox
                  label="Use Subtask for response cloning?"
                  disabled={fetchTasks.isLoading}
                  checked={filters.useSubtaskForCloning}
                  onChange={(e, checked) => handleChange('useSubtaskForCloning', checked)}
                />
              </section>
            </section>
          )}
          <section>
            {filters.useSubtaskForCloning && (
              <Label>
                Params + content will be pulled from the subtask. Please use <b>subtask ids</b>. For
                lego lidar, make sure to use the subtask id you are trying to clone
              </Label>
            )}
            <Label>
              Additionally, filter the above criteria to only work on these specific Task/Subtask
              Ids:
            </Label>
            <i>
              A list of {filters.useSubtaskForCloning ? '' : 'Task And/or'} Subtask Ids can only be
              used to further refine the search results from the broader search above.{' '}
            </i>
            <InputText
              disabled={fetchTasks.isLoading}
              type="text"
              value={tasksFilter || ''}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                handleTasksFilter(e.target.value || '');
              }}
            />
          </section>
          <section>
            <Button disabled={fetchTasks.isLoading} onClick={() => handleSearch()}>
              Search
            </Button>
          </section>

          <h5>Tasks found: {fetchTasks.tasks.length}</h5>
        </div>
        <style jsx>{`
          .root .two-columns {
            display: flex;
          }
          .root .two-columns section {
            flex-basis: 50%;
          }
          .root .two-columns section.disabled {
            color: rgba(0, 0, 0, 0.3);
          }
          .root .section-rows {
            display: flex;
            flex-direction: column;
            gap: 8px;
          }
          .root .three-columns {
            display: flex;
            gap: 16px;
          }
          .root .three-columns section {
            flex-basis: 33%;
          }
          .root .three-columns section.disabled {
            color: rgba(0, 0, 0, 0.3);
          }
          .root .disallowed-task-types {
            font-weight: bold;
            color: red;
          }
          .root .warning {
            font-weight: bold;
            color: red;
          }
          .root.disabled section {
            color: rgba(0, 0, 0, 0.3);
          }
        `}</style>
      </div>
    </Section>
  );
};

export default QueryTasks;
